import { Entity } from '@square/ignition';
import { ApiReferenceFieldContextProvider } from '@squareup/dex-ui-shared-api-reference';
import { ModalWideContent } from '@squareup/dex-ui-shared-market';
import { usePathname } from '@squareup/dex-utils-shared-routing-hooks';
import React, {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReferenceObjectModalContents } from '../../ReferenceObjectModalContents';

interface ReferenceObjectModalContext {
  open: (entity: Entity, baseEntityStack?: Entity[]) => void;
  close: () => void;
}

interface ReferenceObjectModalProviderProps {
  children: ReactNode;
}

const Context = createContext<ReferenceObjectModalContext>({
  open: (_) => null,
  close: () => null,
});

/*
  Custom hook to grab the ReferenceObjectModalContext,
  which is used to open and close 
 */
const useReferenceObjectModalContext = () => {
  return useContext(Context);
};

/*
  A component to manage the modal that shows objects and enums in it.
  It will also keep track of the breadcrumbs for you.
 */
const ReferenceObjectModalProvider: FunctionComponent<
  ReferenceObjectModalProviderProps
> = ({ children }) => {
  const [currentEntity, setCurrentEntity] = useState<Entity | null>(null);
  const [entityStack, setEntityStack] = useState<Entity[]>([]);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const { pathName } = usePathname();

  const open = useCallback(
    (entity: Entity, baseEntityStack?: Entity[]) => {
      if (currentEntity) {
        // We need to account for when someone is in a modal with an existing stack,
        // and then tries to jump forward many nested objects.
        // The order is you take the existing stack, called `entityStack`, the
        // previous entity, called `currentEntity`, and the new stack if the
        // user jumped many nested objects, called `baseEntityStack`.
        const baseStack = baseEntityStack || [];
        setEntityStack([...entityStack, currentEntity, ...baseStack]);
      } else if (baseEntityStack) {
        setEntityStack([...baseEntityStack]);
      }

      setCurrentEntity(entity);
    },
    [currentEntity, entityStack]
  );

  const close = useCallback(() => {
    setEntityStack([]);
    setCurrentEntity(null);
  }, []);

  const onBreadcrumbClicked = useCallback(
    (index: number) => {
      const entity = entityStack[index];
      if (!entity) {
        return;
      }

      setCurrentEntity(entity);
      setEntityStack(entityStack.slice(0, index));
    },
    [entityStack]
  );

  // Ensure we close the modal when the user navigates away
  // We intentionally only run this when the path closes,
  // not when the entity or close method changes, because
  // we only close it when we navigate away.
  useEffect(() => {
    if (currentEntity) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  // Scroll to the top when the entity changes
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [currentEntity]);

  const providerMemo = useMemo(() => {
    return {
      open,
      close,
    };
  }, [close, open]);

  const fieldProviderMemo = useMemo(() => {
    return {
      openModal: open,
    };
  }, [open]);

  return (
    <>
      <Context.Provider value={providerMemo}>
        <ApiReferenceFieldContextProvider value={fieldProviderMemo}>
          {children}
          {currentEntity && (
            <ModalWideContent
              data-testid="object-modal"
              onMarketDialogDismissed={close}
            >
              <ReferenceObjectModalContents
                entity={currentEntity}
                breadcrumbs={entityStack}
                onBreadcrumbClicked={onBreadcrumbClicked}
                onLinkAwayAction={close}
                mainRef={mainRef}
              />
            </ModalWideContent>
          )}
        </ApiReferenceFieldContextProvider>
      </Context.Provider>
    </>
  );
};

export { ReferenceObjectModalProvider, useReferenceObjectModalContext };
